import { supabase } from "@/integrations/supabase/client";
import { CapacitorUtils } from "@/utils/capacitor";

export interface DeviceRegistrationResult {
  success: boolean;
  error?: string;
  token?: string;
}

export class DeviceRegistrationService {
  static async registerCurrentDevice(userId: string): Promise<DeviceRegistrationResult> {
    try {
      console.log('🔔 Starting device registration for user:', userId);
      console.log('🌐 Platform:', CapacitorUtils.getPlatform());
      console.log('📱 Is native:', CapacitorUtils.isNative());
      
      // Clear any old device tokens first (necessary after VAPID key change)
      console.log('🧹 Cleaning up old device registrations...');
      await this.clearOldTokens(userId);
      
      // Get device token with new VAPID keys
      console.log('⏳ Getting device token with updated VAPID keys...');
      const token = await CapacitorUtils.getDeviceToken();
      
      if (!token) {
        console.error('❌ Failed to get device token');
        return {
          success: false,
          error: 'Failed to get device token. Please check browser permissions and try again.'
        };
      }

      console.log('📱 Device token obtained, length:', token.length);
      console.log('📱 Token preview:', token.substring(0, 50) + '...');

      // Determine device type
      const deviceType = this.getDeviceType();
      console.log('📱 Device type:', deviceType);
      
      // Register device in database
      console.log('💾 Saving device to database...');
      const { data, error } = await supabase
        .from('user_devices')
        .insert({
          user_id: userId,
          device_token: token,
          device_type: deviceType,
          is_active: true,
        })
        .select()
        .single();

      if (error) {
        console.error('❌ Database error registering device:', error);
        console.error('Error details:', JSON.stringify(error, null, 2));
        return {
          success: false,
          error: `Database error: ${error.message}`
        };
      }

      console.log('✅ Device registered successfully in database');
      console.log('📦 Database response:', data);
      
      return {
        success: true,
        token
      };
    } catch (error) {
      console.error('❌ Device registration failed with exception:', error);
      console.error('Exception details:', JSON.stringify(error, null, 2));
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  }

  // Helper to clear old/invalid tokens (including those from old VAPID keys)
  private static async clearOldTokens(userId: string): Promise<void> {
    try {
      // Mark all existing devices as inactive for this user
      // This is necessary after VAPID key changes as old tokens become invalid
      const { error } = await supabase
        .from('user_devices')
        .update({ is_active: false })
        .eq('user_id', userId);

      if (error) {
        console.warn('⚠️ Warning: Could not clear old tokens:', error);
      } else {
        console.log('✅ Old device tokens cleared (required after VAPID key update)');
      }
    } catch (error) {
      console.warn('⚠️ Warning: Error clearing old tokens:', error);
    }
  }

  // Force re-registration for all users (called after VAPID key update)
  static async forceGlobalReregistration(): Promise<void> {
    try {
      console.log('🔄 Force clearing all device tokens due to VAPID key update...');
      const { error } = await supabase
        .from('user_devices')
        .update({ is_active: false })
        .neq('id', '00000000-0000-0000-0000-000000000000'); // Update all records

      if (error) {
        console.warn('⚠️ Warning: Could not force global re-registration:', error);
      } else {
        console.log('✅ All device tokens cleared - users will need to re-enable notifications');
      }
    } catch (error) {
      console.warn('⚠️ Warning: Error in force global re-registration:', error);
    }
  }

  static async unregisterCurrentDevice(userId: string): Promise<boolean> {
    try {
      const token = await CapacitorUtils.getDeviceToken();
      if (!token) {
        return false;
      }

      const { error } = await supabase
        .from('user_devices')
        .update({ is_active: false })
        .eq('user_id', userId)
        .eq('device_token', token);

      if (error) {
        console.error('Error unregistering device:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error unregistering device:', error);
      return false;
    }
  }

  static async refreshDeviceToken(userId: string): Promise<DeviceRegistrationResult> {
    try {
      // First unregister old token (if any)
      await this.unregisterCurrentDevice(userId);
      
      // Then register with new token
      return await this.registerCurrentDevice(userId);
    } catch (error) {
      console.error('Error refreshing device token:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  private static getDeviceType(): string {
    if (CapacitorUtils.isNative()) {
      const platform = CapacitorUtils.getPlatform();
      return platform === 'ios' ? 'ios' : 'android';
    }
    return 'web';
  }

  static async getCurrentDeviceInfo(): Promise<{
    token: string | null;
    type: string;
    isNative: boolean;
  }> {
    const token = await CapacitorUtils.getDeviceToken();
    return {
      token,
      type: this.getDeviceType(),
      isNative: CapacitorUtils.isNative()
    };
  }

  static setupTokenRefreshListener(userId: string) {
    console.log('🔄 Setting up token refresh listener for user:', userId);
    
    CapacitorUtils.setupTokenRefreshListener(async (newToken: string) => {
      console.log('🔄 Device token refreshed, updating database...');
      
      const deviceType = this.getDeviceType();
      
      try {
        const { error } = await supabase
          .from('user_devices')
          .upsert(
            {
              user_id: userId,
              device_token: newToken,
              device_type: deviceType,
              is_active: true,
            },
            { 
              onConflict: 'user_id,device_token',
              ignoreDuplicates: false 
            }
          );

        if (error) {
          console.error('❌ Error updating device token:', error);
        } else {
          console.log('✅ Device token updated successfully');
        }
      } catch (error) {
        console.error('❌ Failed to update device token:', error);
      }
    });
  }

  static removeTokenRefreshListener() {
    console.log('🔄 Removing token refresh listeners');
    CapacitorUtils.removeTokenRefreshListeners();
  }
}